import { clsx } from 'clsx';
import { useRef, useState } from 'react';

import { FileDropZone } from 'ui/FileDrop/FileDropZone';
import { prepareImageFile } from 'utils/prepareImageFile';

import { useCameraStream } from './useCameraStream';
import type { CaptureStepConfig } from '../types';

import classes from '../PassportCaptureWizard.module.scss';

const GALLERY_ACCEPT =
  'image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif';

interface DocumentCameraProps {
  config: CaptureStepConfig;
  onCapture: (blob: Blob, source: 'camera' | 'gallery') => void;
  onBack: () => void;
  active: boolean;
  showBack?: boolean;
}

export const DocumentCamera = ({
  config,
  onCapture,
  onBack,
  active,
}: DocumentCameraProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [galleryError, setGalleryError] = useState<string | null>(null);
  const [galleryBusy, setGalleryBusy] = useState(false);
  const { videoRef, error, isReady, captureFrame } = useCameraStream({
    facingMode: config.facingMode,
    enabled: active,
  });

  const stepLabel = `Шаг ${config.stepNumber}/3`;
  const bannerError = galleryError || error;

  const handleCapture = async () => {
    setGalleryError(null);
    const blob = await captureFrame();
    if (blob) {
      onCapture(blob, 'camera');
    }
  };

  const takeGalleryFile = async (file: File) => {
    setGalleryBusy(true);
    setGalleryError(null);
    try {
      const prepared = await prepareImageFile(file);
      onCapture(prepared, 'gallery');
    } catch (err) {
      setGalleryError(
        err instanceof Error
          ? err.message
          : 'Выберите файл JPEG, PNG или HEIC. Другие форматы не принимаются.',
      );
    } finally {
      setGalleryBusy(false);
    }
  };

  const handleGalleryChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    await takeGalleryFile(file);
  };

  return (
    <div className={classes.cameraScreen}>
      <FileDropZone
        className={classes.passportSheet}
        accept={GALLERY_ACCEPT}
        disabled={galleryBusy}
        hint="Отпустите фото, чтобы загрузить"
        onFiles={(files) => {
          const file = files[0];
          if (file) void takeGalleryFile(file);
        }}
        aria-labelledby='passport-sheet-title'
      >
        <p className={classes.passportStep}>{stepLabel}</p>
        <h1 id='passport-sheet-title'>{config.title}</h1>
        <p>Расположите документ внутри рамки перед снимком.</p>
        <div className={classes.passportPreview}>
          <video
            ref={videoRef}
            className={clsx(
              classes.passportVideo,
              config.facingMode === 'user' && classes.videoMirrored,
            )}
            playsInline
            muted
            autoPlay
          />
          <div className={classes.passportFrame}>
            <span>{config.hint}</span>
          </div>
        </div>
        {bannerError ? (
          <p className={classes.passportError} role='alert'>
            {bannerError}
          </p>
        ) : null}
        <div className={classes.passportActions}>
          <button type='button' className={classes.passportCancel} onClick={onBack}>
            Отмена
          </button>
          <button
            type='button'
            className={classes.passportShot}
            onClick={() => void handleCapture()}
            disabled={!isReady || Boolean(error) || galleryBusy}
          >
            Сделать снимок
          </button>
        </div>
        <button
          type='button'
          className={classes.passportGallery}
          onClick={() => {
            setGalleryError(null);
            fileInputRef.current?.click();
          }}
          disabled={galleryBusy}
        >
          Загрузить из галереи
        </button>
        <input
          ref={fileInputRef}
          type='file'
          accept={GALLERY_ACCEPT}
          className={classes.hiddenInput}
          onChange={(e) => void handleGalleryChange(e)}
        />
      </FileDropZone>
    </div>
  );
};
